import React, { FC, FunctionComponent, useEffect, useRef } from 'react';
import { Status } from '../../../../types/redux/data/dataTypes';
import { greys, mainColors } from '../../../../styling/theme';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CachedIcon from '@mui/icons-material/Cached';
import Button from '@mui/material/Button';
import hotToast from 'react-hot-toast';
import { Theme, Tooltip, Typography } from '@mui/material';
import { toast } from '../../../toast/Toast';
import { makeStyles } from '@mui/styles';
import TokenService from '../../../../services/token-service';
import {
  KidDocumentFigure,
  useKIDSDocumentFigures,
  useKIDSPublishedCurrentFigures,
} from '../kids/services/queries';
import { KidComparisonEntry } from '../kids/types/KidsData.types';
import CompareFiguresStatusBox from './CompareFiguresStatusBox';
import { useQueryClient } from '@tanstack/react-query';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { sortFundNames } from '../../../../utilities/customSorting';
import {
  KidFiguresPartialSuccessResponse,
  useWriteCurrentKidFiguresToDocument,
} from '../mutations';

interface KidsFiguresData {
  fundId: string;
  fundName: string;
  shareClass: string;
  sriCurrent: number | string;
  sriPublished: number | string;
  srriDocument?: number;
  sriStatus: Status;
  averageModerateReturnsCurrent: number | string;
  averageModerateReturnsPublished: number | string;
  averageModerateReturnsDocument?: number;
  averageModerateReturnsStatus: Status;
  documentDate?: string;
  publishedDate: string | string;
  calculationDate: string | string;
  dateStatus: Status;
  overallStatus: Status;
  rowColor: string;
  isLoadingDocuments: boolean;
}

interface StyleProps {
  status: keyof typeof mainColors;
  statusDarker: keyof typeof mainColors;
  color: string | null;
  neutralColor?: boolean;
  disabled?: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  PageLinkButtonRoot: {
    backgroundColor: (props: StyleProps) =>
      props.disabled
        ? greys.grey700
        : props.neutralColor
          ? mainColors.mainBlue_lighter
          : mainColors[props.status],
    color: (props: StyleProps) => (props.color ? props.color : 'white'),
    maxWidth: '12.50rem',
    whiteSpace: 'nowrap',
    padding: '8px',
    fontweight: 500,
    borderRadius: 4,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    textAlign: 'center',
    margin: '0 auto',
    width: '100%',
  },
}));

export const OverallStatusBox = ({
  status,
}: {
  status: Status;
  neutralColor?: boolean;
  disabled?: boolean;
}) => {
  const classes = useStyles({
    status,
    statusDarker: `${status}_darker`,
  } as StyleProps);

  return <div className={classes.PageLinkButtonRoot}>{status}</div>;
};

function buildKidsFiguresData(
  data: KidComparisonEntry[],
  fundIdFilter: string,
  documentFigures: KidDocumentFigure[],
  isLoadingDocuments: boolean,
): KidsFiguresData[] {
  const tableData: KidsFiguresData[] = [];

  // Filter and sort share classes by ISIN (share_class)
  let filtered = data
    .filter((entry) => {
      if (fundIdFilter === '') return true;
      return entry.fund_id === fundIdFilter;
    })
    .sort((a, b) => a.share_class.localeCompare(b.share_class));

  let currentFundId = filtered[0]?.fund_id;
  let currentColorKey = true;

  const colourOptions: Record<string, string> = {
    true: '#ffffff',
    false: mainColors.controlButtonBlue,
  };

  filtered.forEach((entry) => {
    const { comparison_result } = entry;

    const documentValues = documentFigures.find((doc) => {
      return doc.share_class === entry.share_class;
    });

    // Toggle zebra stripe color when fund ID changes
    if (currentFundId !== entry.fund_id) {
      currentFundId = entry.fund_id;
      currentColorKey = !currentColorKey;
    }

    tableData.push({
      fundId: entry.fund_id,
      fundName: entry.fund_name,
      shareClass: entry.share_class,

      sriCurrent: comparison_result.SRI.current_value ?? 'No data',
      sriPublished: comparison_result.SRI.published_value ?? 'No data',
      srriDocument: documentValues?.SRI,
      sriStatus: comparison_result.SRI.status as Status,

      averageModerateReturnsCurrent:
        comparison_result.average_moderate_return?.current_value ?? 'No data',
      averageModerateReturnsPublished:
        comparison_result.average_moderate_return?.published_value ?? 'No data',

      averageModerateReturnsDocument: documentValues?.monthly_return,
      averageModerateReturnsStatus:
        (comparison_result.average_moderate_return?.status as Status) ??
        Status.NA,

      publishedDate:
        comparison_result.figures_date?.published_value ?? 'No data',

      documentDate: documentValues?.date,

      calculationDate:
        comparison_result.figures_date?.current_value ?? 'No data',
      dateStatus:
        (comparison_result.figures_date?.status as Status) ?? Status.NA,

      overallStatus: comparison_result.overall_status as Status,
      rowColor: colourOptions[String(currentColorKey)],
      isLoadingDocuments: isLoadingDocuments,
    });
  });

  return tableData;
}

function buildColumns(): CustomColumn<KidsFiguresData>[] {
  const columns: CustomColumn<KidsFiguresData>[] = [
    {
      title: 'Fund ID',
      field: 'fundId',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: { textAlign: 'center' },
    },
    {
      title: 'Fund Name',
      field: 'fundName',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: { textAlign: 'center' },
    },
    {
      title: 'Share Class',
      field: 'shareClass',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: { textAlign: 'center' },
    },
    {
      title: (
        <div className="flex gap-1">
          <div className="text-nowrap">SRI</div>
          <Tooltip
            title="An alert is triggered if the value has changed"
            placement="top"
          >
            <HelpOutlineIcon
              fontSize="small"
              className="align-middle"
              style={{ fontSize: 16, verticalAlign: 'middle' }}
            />
          </Tooltip>
        </div>
      ),
      field: 'sriPublished',
      cellStyle: {
        textAlign: 'center',
        width: '12%',
      },
      headerStyle: {
        textAlign: 'center',
        width: '12%',
      },
      render: (rowData: KidsFiguresData) => (
        <CompareFiguresStatusBox
          currentValue={rowData.sriCurrent}
          publishedValue={rowData.sriPublished}
          documentValue={rowData.srriDocument}
          status={rowData.sriStatus}
          isLoadingDocuments={rowData.isLoadingDocuments}
        />
      ),
    },
    {
      title: (
        <div className="flex gap-1">
          <div className="text-nowrap">Average Moderate Returns</div>
          <Tooltip
            title="An alert is triggered if the current value moves by more than 0.05 from the published value."
            placement="top"
          >
            <HelpOutlineIcon
              fontSize="small"
              className="align-middle"
              style={{ fontSize: 16, verticalAlign: 'middle' }}
            />
          </Tooltip>
        </div>
      ),
      field: 'averageModerateReturnsPublished',
      cellStyle: {
        textAlign: 'center',
        width: '12%',
      },
      headerStyle: {
        textAlign: 'center',
        width: '12%',
      },
      render: (rowData: KidsFiguresData) => (
        <CompareFiguresStatusBox
          currentValue={rowData.averageModerateReturnsCurrent}
          publishedValue={rowData.averageModerateReturnsPublished}
          documentValue={rowData.averageModerateReturnsDocument}
          status={rowData.averageModerateReturnsStatus}
          isLoadingDocuments={rowData.isLoadingDocuments}
        />
      ),
    },
    {
      title: (
        <div className="flex gap-1">
          <div className="text-nowrap">Figures Date</div>
          <Tooltip
            title="An alert is triggered if the published date is one year older than the current date."
            placement="top"
          >
            <HelpOutlineIcon
              fontSize="small"
              className="align-middle"
              style={{ fontSize: 16, verticalAlign: 'middle' }}
            />
          </Tooltip>
        </div>
      ),
      field: 'publishedDate',
      cellStyle: {
        textAlign: 'center',
        width: '12%',
      },
      headerStyle: {
        textAlign: 'center',
        width: '12%',
      },
      render: (rowData: KidsFiguresData) => (
        <CompareFiguresStatusBox
          currentValue={rowData.calculationDate}
          publishedValue={rowData.publishedDate}
          documentValue={rowData.documentDate}
          status={rowData.dateStatus}
          isLoadingDocuments={rowData.isLoadingDocuments}
        />
      ),
    },
    {
      title: (
        <div className="flex gap-1">
          <div className="text-nowrap">Overall Status</div>
          <Tooltip
            title="If there is any alert, then overall status is alert"
            placement="top"
          >
            <HelpOutlineIcon
              fontSize="small"
              className="align-middle"
              style={{ fontSize: 16, verticalAlign: 'middle' }}
            />
          </Tooltip>
        </div>
      ),
      field: 'overallStatus',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: { textAlign: 'center' },
      render: (rowData: KidsFiguresData) => (
        <OverallStatusBox status={rowData.overallStatus} />
      ),
    },
  ];
  return columns;
}

interface FundIdentifiers {
  fundId: string;
  fundName: string;
  fundUUID: string;
}

function getUniqueFundIds(
  data: KidComparisonEntry[] | undefined,
): FundIdentifiers[] {
  if (!Array.isArray(data) || data.length === 0) return [];

  const fundIdentifiers: FundIdentifiers[] = [];
  const seenFundIds = new Set<string>();

  data.forEach((element, index) => {
    const { fund_id, fund_name, fund_uuid } = element || {};

    if (fund_id && fund_name && fund_uuid) {
      if (!seenFundIds.has(fund_id)) {
        seenFundIds.add(fund_id);
        fundIdentifiers.push({
          fundId: fund_id,
          fundName: fund_name,
          fundUUID: fund_uuid,
        });
      }
    }
  });

  // Sort by fundId using sortFundNames
  fundIdentifiers.sort((a, b) => sortFundNames(a.fundId, b.fundId));

  return fundIdentifiers;
}

interface KidFiguresTableProps {
  tableData: KidsFiguresData[];
  fundIdentifiers: FundIdentifiers;
  clientId: string;
}

const KidFiguresTable = ({
  tableData,
  fundIdentifiers,
  clientId,
}: KidFiguresTableProps) => {
  const columns = buildColumns();

  return (
    <>
      <div className="flex justify-between">
        <Typography
          variant="h2"
          style={{
            marginBottom: '0.62rem',
            marginLeft: '0.62rem',
            marginTop: '0.62rem',
          }}
        >{`KIDS Figures - ${fundIdentifiers.fundName}`}</Typography>
        <div
          style={{
            marginRight: '0.62rem',
            marginTop: '0.62rem',
          }}
        >
          <RefreshFiguresButton
            fundUUID={fundIdentifiers.fundUUID}
            fundName={fundIdentifiers.fundName}
            clientId={clientId}
          />
        </div>
      </div>
      <CustomTable<KidsFiguresData>
        title={'KIDS Figures'}
        showToolbar={false}
        id={`kiids_editor_overview_table`}
        data={tableData}
        options={{
          paging: false,
          search: false,
          exportButton: false,
          exportAllData: false,
          emptyRowsWhenPaging: false,
          draggable: false,
        }}
        columns={columns}
      />
    </>
  );
};

interface RefreshFiguresButtonProps {
  fundUUID: string;
  fundName: string;
  clientId: string;
}

export function RefreshFiguresButton({
  fundUUID,
  fundName,
  clientId,
}: RefreshFiguresButtonProps): React.ReactElement {
  const loadingToastId = useRef<string | undefined>(undefined);
  const queryClient = useQueryClient();

  const { mutate, isPending, error } = useWriteCurrentKidFiguresToDocument(
    clientId,
    {
      onMutate: () => {
        loadingToastId.current = toast({
          title: 'Refreshing Figures...',
          message: `Updating figures for ${fundName}. Please wait...`,
          type: 'loading',
        });
      },
      onSuccess: (response) => {
        if (response.status === 207) {
          const data = response.data as KidFiguresPartialSuccessResponse;
          const failedShareClasses = data.failed_share_classes || [];
          toast({
            title: 'Partial Update!',
            message: (
              <span className="flex flex-col">
                <div className="mb-1">
                  Figures were updated for all share classes except:
                </div>
                {failedShareClasses.map((shareClass: string, index: number) => (
                  <div key={index} className="flex">
                    <div className="font-medium">Failed Class:</div>
                    <div className="flex-1 break-words italic">
                      {shareClass}
                    </div>
                  </div>
                ))}
                <div className="mt-2">
                  All other share classes were updated successfully.
                </div>
              </span>
            ),
            type: 'error',
            duration: 2 * 60 * 1000,
          });
        } else {
          toast({
            title: 'Success!',
            message: `Figures updated successfully.`,
            type: 'success',
            duration: 5000,
          });
        }
        // Invalidate the kids-document-figures query
        queryClient.invalidateQueries({
          queryKey: ['kids-document-figures', clientId],
        });
        // invalidate the kids-overview-data query
        queryClient.invalidateQueries({
          queryKey: ['kids-overview-data'],
        });
      },
      onSettled: () => {
        if (loadingToastId.current) {
          hotToast.dismiss(loadingToastId.current);
          loadingToastId.current = undefined;
        }
      },
    },
  );

  async function handleFiguresUpdate() {
    const confirm = window.confirm(
      `Updating will update figures for all share classes of ${fundName}. Do you wish to continue?`,
    );
    if (!confirm) return;

    const payload = {
      fund_id: fundUUID,
    };

    mutate(payload);
  }

  return (
    <Button
      variant="contained"
      startIcon={<CachedIcon />}
      onClick={handleFiguresUpdate}
      disabled={isPending}
      color={error ? 'error' : 'primary'}
    >
      {isPending
        ? 'Updating...'
        : error
          ? 'Retry Updating KIDs'
          : 'Load current figures to Documents'}
    </Button>
  );
}

const KidsFigures: FC = () => {
  const user = TokenService.getUser()!;
  const clientId = user.user_client_id;

  // Fetch published figures
  const {
    data: publishedFigures,
    isPending,
    isError,
  } = useKIDSPublishedCurrentFigures(clientId);

  const fundIdentifiers = getUniqueFundIds(publishedFigures);

  return (
    <QueryBoundary
      isLoading={isPending}
      loadingMessages={[
        'Loading KID published figures...',
        'This may take a while...',
      ]}
      isError={isError}
      data={publishedFigures}
      errorMessage="Failed to load KID published figures."
      noDataMessage="No KID figures available."
    >
      {(data) => (
        <main className="grid grid-cols-1 gap-4 p-1">
          <DocumentFetch
            fundIdentifiers={fundIdentifiers}
            publishedFigures={data}
            clientId={clientId}
          />
        </main>
      )}
    </QueryBoundary>
  );
};

export default KidsFigures;

interface DocumentFetchProps {
  fundIdentifiers: FundIdentifiers[];
  publishedFigures: KidComparisonEntry[];
  clientId: string;
}

const DocumentFetch: FunctionComponent<DocumentFetchProps> = ({
  fundIdentifiers,
  publishedFigures,
  clientId,
}) => {
  // Fetch document figures
  const { data, isFetching } = useKIDSDocumentFigures(clientId);

  return (
    <>
      {fundIdentifiers.map((item) => (
        <GridItem key={item.fundId} xs={12} card>
          <KidFiguresTable
            tableData={buildKidsFiguresData(
              publishedFigures,
              item.fundId,
              data || [],
              isFetching,
            )}
            fundIdentifiers={item}
            clientId={clientId}
          />
        </GridItem>
      ))}
    </>
  );
};
